import { PlusIcon, XIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { GoalFormState, SlotDraft } from "../../types";
import { defaultSlot } from "../../utils";

interface SlotEditorProps {
  readonly form: GoalFormState;
  readonly setForm: (value: GoalFormState | ((previous: GoalFormState) => GoalFormState)) => void;
}

export function SlotEditor({ form, setForm }: SlotEditorProps) {
  const updateSlot = (index: number, field: keyof SlotDraft, value: string | number | boolean) => {
    setForm((previous) => ({
      ...previous,
      slots: previous.slots.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot,
      ),
    }));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Time slots</Label>
        <button
          type="button"
          onClick={() => setForm((previous) => ({ ...previous, slots: [...previous.slots, defaultSlot()] }))}
          className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
        >
          <PlusIcon className="w-3 h-3" /> Add slot
        </button>
      </div>

      <div className="space-y-2">
        {form.slots.map((slot, index) => (
          <div key={index} className="space-y-1.5 rounded-lg border border-border/60 p-2">
            <div className="flex items-center gap-2">
              <Input
                value={slot.name}
                onChange={(event) => updateSlot(index, "name", event.target.value)}
                placeholder="Slot name (e.g. Morning)"
                className="flex-1"
              />
              <input
                type="time"
                value={slot.dueTimeLocal}
                onChange={(event) => updateSlot(index, "dueTimeLocal", event.target.value)}
                className="h-8 rounded-lg border border-input bg-transparent px-2 text-sm tabular-nums focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none dark:bg-input/30"
              />
              {form.slots.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setForm((previous) => ({
                      ...previous,
                      slots: previous.slots.filter((_, slotIndex) => slotIndex !== index),
                    }))
                  }
                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Input
              value={slot.recoveryAction}
              maxLength={200}
              onChange={(event) => updateSlot(index, "recoveryAction", event.target.value)}
              placeholder="If missed, do instead: e.g. 10 pushups before bed (optional)"
              className="text-xs"
            />
            <label className="flex items-center gap-2 px-0.5 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={slot.notifySelfOnOpen}
                onChange={(event) => updateSlot(index, "notifySelfOnOpen", event.target.checked)}
                className="accent-primary"
              />
              Remind me when this window opens (mobile app notification)
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
